import type { EventType, JSONValue } from './types'

/**
 * Convert an event into a push-notification summary (title + body).
 * Returns null for events that shouldn't push (corrections, person lifecycle, family lifecycle).
 * Called from emitEvent to decide whether to fan out.
 */
export function pushSummaryForEvent(
  eventType: EventType,
  payload: JSONValue,
): { title: string; body: string; url?: string; tag?: string } | null {
  const p = (typeof payload === 'object' && payload !== null && !Array.isArray(payload)) ? (payload as Record<string, unknown>) : {}

  switch (eventType) {
    case 'early_care.feeding.recorded': {
      const type = String(p['feed_type'] ?? 'feed')
      const vol = typeof p['volume_ml'] === 'number' ? Math.round(p['volume_ml'] as number) : null
      const dur = typeof p['duration_minutes'] === 'number' ? (p['duration_minutes'] as number) : null
      const detail = vol != null ? `${vol} mL` : dur != null ? `${dur} min` : ''
      return {
        title: 'Feed logged',
        body: `${prettyFeed(type)}${detail ? ' · ' + detail : ''}`,
        url: '/timeline',
        tag: 'feed',
      }
    }
    case 'early_care.diaper.recorded': {
      const type = String(p['diaper_type'] ?? 'diaper')
      return { title: 'Diaper logged', body: `${cap(type)} diaper`, url: '/timeline', tag: 'diaper' }
    }
    case 'early_care.sleep.started': {
      const loc = p['location'] ? ` in the ${p['location']}` : ''
      return { title: 'Nap started', body: `Sleeping${loc}`, url: '/timeline', tag: 'sleep' }
    }
    case 'early_care.sleep.ended':
      return { title: 'Nap ended', body: 'Woke up', url: '/timeline', tag: 'sleep' }
    case 'early_care.bath.recorded':
      return { title: 'Bath logged', body: 'Bath time recorded', url: '/timeline', tag: 'bath' }
    case 'early_care.temperature.recorded': {
      const v = p['value']
      const u = p['unit']
      return { title: 'Temp logged', body: `${v}°${u ?? ''}`, url: '/timeline', tag: 'temp' }
    }
    case 'early_care.solid.recorded': {
      const f = String(p['food'] ?? 'solid food')
      return { title: 'Solid food', body: f, url: '/timeline', tag: 'solid' }
    }
    case 'early_care.pump.recorded': {
      const total = p['volume_ml_total']
      const dur = p['duration_minutes']
      const parts: string[] = []
      if (typeof total === 'number') parts.push(`${total} mL`)
      if (typeof dur === 'number') parts.push(`${dur} min`)
      return { title: 'Pump session', body: parts.join(' · ') || 'Session logged', url: '/timeline', tag: 'pump' }
    }
    case 'early_care.medication.recorded': {
      const name = String(p['medication_name'] ?? 'Medication')
      const amt = p['amount']
      const unit = p['amount_unit']
      const dose = amt != null ? ` · ${amt} ${unit ?? ''}`.trim() : ''
      return { title: 'Medication given', body: `${name}${dose}`, url: '/timeline', tag: 'medication' }
    }
    case 'growth.recorded': {
      const mt = String(p['measurement_type'] ?? 'measurement')
      const v = p['value']
      const u = p['unit']
      return { title: 'Growth logged', body: `${cap(mt)}: ${v} ${u ?? ''}`.trim(), url: '/timeline', tag: 'growth' }
    }
    case 'note.created': {
      const text = typeof p['text'] === 'string' ? (p['text'] as string) : ''
      return { title: 'Note added', body: text.slice(0, 100), url: '/timeline', tag: 'note' }
    }
    case 'need.created': {
      const title = typeof p['title'] === 'string' ? (p['title'] as string) : 'New need'
      return { title: 'New need', body: title.slice(0, 100), url: '/needs', tag: 'need' }
    }
    case 'family.handoff.created': {
      return { title: 'Handoff', body: 'Care has been handed off', url: '/timeline', tag: 'handoff' }
    }
    default:
      return null
  }
}

function prettyFeed(k: string): string {
  switch (k) {
    case 'bottle_formula': return 'Bottle · formula'
    case 'bottle_breastmilk': return 'Bottle · breast milk'
    case 'breast_left': return 'Breast · left'
    case 'breast_right': return 'Breast · right'
    case 'both': return 'Both breasts'
    case 'solid': return 'Solid food'
    default: return 'Feed'
  }
}
function cap(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s
}
